/* eslint-disable react-refresh/only-export-components */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { updateMySettings } from '../services/api';
import type { BackgroundThemeId } from '../types';
import { useAuth } from './AuthContext';
import {
  BACKGROUND_THEME_INTENSITY,
  clampIntensity,
  DEFAULT_BACKGROUND_THEME,
  resolveBackgroundTheme,
} from '../components/backgrounds/backgroundThemes';
import { useNotification } from '../hooks/useNotification';

interface AppearanceContextValue {
  canCustomizeBackgroundTheme: boolean;
  backgroundTheme: BackgroundThemeId;
  backgroundThemeIntensity: number;
  backgroundActivity: 'normal' | 'low';
  setBackgroundTheme: (id: BackgroundThemeId) => Promise<void>;
  setBackgroundThemeIntensity: (value: number) => void;
  reportBackgroundActivity: (active: boolean) => () => void;
  hasPageBackground: boolean;
  registerPageBackground: () => () => void;
}

interface CachedBackgroundTheme {
  theme?: unknown;
  intensity?: unknown;
}

const BOOT_CACHE_KEY = 'user-background-theme';
const AppearanceContext = createContext<AppearanceContextValue | undefined>(undefined);

const dispatchUserSettingUpdated = (detail: {
  backgroundTheme?: BackgroundThemeId;
  backgroundThemeIntensity?: number;
}) => {
  window.dispatchEvent(new CustomEvent('auth:user-setting-updated', { detail }));
};

const writeBootCache = (value: { theme: BackgroundThemeId; intensity: number } | null) => {
  if (typeof window === 'undefined') return;

  try {
    if (value) {
      window.localStorage.setItem(BOOT_CACHE_KEY, JSON.stringify(value));
    } else {
      window.localStorage.removeItem(BOOT_CACHE_KEY);
    }
  } catch {
    // Storage can be unavailable (private mode); the cache is only a boot-time hint.
  }
};

const readBootCache = (): { theme: BackgroundThemeId; intensity: number } | null => {
  if (typeof window === 'undefined') return null;

  try {
    const rawValue = window.localStorage.getItem(BOOT_CACHE_KEY);
    if (!rawValue) return null;

    const parsed = JSON.parse(rawValue) as CachedBackgroundTheme;
    return {
      theme: resolveBackgroundTheme(parsed.theme).id,
      intensity: clampIntensity(parsed.intensity),
    };
  } catch {
    return null;
  }
};

interface AppearanceProviderProps {
  children: React.ReactNode;
}

export const AppearanceProvider: React.FC<AppearanceProviderProps> = ({ children }) => {
  const { t } = useTranslation();
  const { user, isAnonymous, isLoading } = useAuth();
  const { error, success } = useNotification();
  const cacheRef = useRef(readBootCache());
  const debounceRef = useRef<ReturnType<typeof window.setTimeout> | null>(null);
  const activityCountRef = useRef(0);
  const [backgroundActivity, setBackgroundActivity] = useState<'normal' | 'low'>('normal');
  const canCustomizeBackgroundTheme =
    Boolean(user) && !isAnonymous && user?.allowDynamicTheme === true;
  const userTheme = resolveBackgroundTheme(user?.setting?.backgroundTheme).id;
  const userIntensity = clampIntensity(user?.setting?.backgroundThemeIntensity);
  const [localIntensity, setLocalIntensity] = useState<number | null>(null);
  const cachedTheme = isLoading && !user ? cacheRef.current?.theme : undefined;
  const cachedIntensity = isLoading && !user ? cacheRef.current?.intensity : undefined;
  const backgroundTheme = canCustomizeBackgroundTheme
    ? userTheme
    : (cachedTheme ?? DEFAULT_BACKGROUND_THEME);
  const backgroundThemeIntensity = canCustomizeBackgroundTheme
    ? clampIntensity(localIntensity ?? userIntensity)
    : (cachedIntensity ?? BACKGROUND_THEME_INTENSITY.default);

  useEffect(() => {
    if (!canCustomizeBackgroundTheme) {
      setLocalIntensity(null);
    }
  }, [canCustomizeBackgroundTheme]);

  useEffect(() => {
    if (!canCustomizeBackgroundTheme) {
      if (!isLoading) {
        writeBootCache(null);
      }
      return;
    }

    writeBootCache({ theme: backgroundTheme, intensity: backgroundThemeIntensity });
  }, [backgroundTheme, backgroundThemeIntensity, canCustomizeBackgroundTheme, isLoading]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const setBackgroundTheme = useCallback(
    async (id: BackgroundThemeId) => {
      if (!canCustomizeBackgroundTheme) return;

      const nextTheme = resolveBackgroundTheme(id).id;
      const previousTheme = userTheme;
      dispatchUserSettingUpdated({ backgroundTheme: nextTheme });

      const result = await updateMySettings({ backgroundTheme: nextTheme });
      if (!result.success) {
        dispatchUserSettingUpdated({ backgroundTheme: previousTheme });
        error(result.message || t('settings.appearance.messages.backgroundThemeUpdateFailed'));
        return;
      }

      if (result.data) {
        dispatchUserSettingUpdated(result.data);
      }
      success(t('settings.appearance.messages.backgroundThemeUpdated'));
    },
    [canCustomizeBackgroundTheme, error, success, t, userTheme]
  );

  const setBackgroundThemeIntensity = useCallback(
    (value: number) => {
      if (!canCustomizeBackgroundTheme) return;

      const nextIntensity = clampIntensity(value);
      setLocalIntensity(nextIntensity);

      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
      }

      debounceRef.current = window.setTimeout(async () => {
        const result = await updateMySettings({ backgroundThemeIntensity: nextIntensity });
        debounceRef.current = null;

        if (!result.success) {
          setLocalIntensity(userIntensity);
          error(result.message || t('settings.appearance.messages.backgroundThemeUpdateFailed'));
          return;
        }

        dispatchUserSettingUpdated(
          result.data ?? {
            backgroundThemeIntensity: nextIntensity,
          }
        );
        // Keep a newer slider value the user set while this request was in flight.
        setLocalIntensity((current) => (current === nextIntensity ? null : current));
      }, 500);
    },
    [canCustomizeBackgroundTheme, error, t, userIntensity]
  );

  const reportBackgroundActivity = useCallback((active: boolean) => {
    if (!active) {
      return () => undefined;
    }

    activityCountRef.current += 1;
    setBackgroundActivity('low');

    let released = false;
    return () => {
      if (released) return;
      released = true;
      activityCountRef.current = Math.max(0, activityCountRef.current - 1);
      if (activityCountRef.current === 0) {
        setBackgroundActivity('normal');
      }
    };
  }, []);

  const [pageBackgroundCount, setPageBackgroundCount] = useState(0);
  const registerPageBackground = useCallback(() => {
    setPageBackgroundCount((count) => count + 1);
    return () => setPageBackgroundCount((count) => Math.max(0, count - 1));
  }, []);

  const value = useMemo(
    () => ({
      canCustomizeBackgroundTheme,
      backgroundTheme,
      backgroundThemeIntensity,
      backgroundActivity,
      setBackgroundTheme,
      setBackgroundThemeIntensity,
      reportBackgroundActivity,
      hasPageBackground: pageBackgroundCount > 0,
      registerPageBackground,
    }),
    [
      pageBackgroundCount,
      registerPageBackground,
      backgroundActivity,
      backgroundTheme,
      backgroundThemeIntensity,
      canCustomizeBackgroundTheme,
      reportBackgroundActivity,
      setBackgroundTheme,
      setBackgroundThemeIntensity,
    ]
  );

  return <AppearanceContext.Provider value={value}>{children}</AppearanceContext.Provider>;
};

export function useAppearance(): AppearanceContextValue {
  const context = useContext(AppearanceContext);
  if (!context) {
    throw new Error('useAppearance must be used within AppearanceProvider');
  }
  return context;
}

export function useReportBackgroundActivity(active: boolean): void {
  const { reportBackgroundActivity } = useAppearance();

  useEffect(() => {
    return reportBackgroundActivity(active);
  }, [active, reportBackgroundActivity]);
}
